import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePortfoliosQuery } from '../api';
import { AssignmentDialog, type AssignmentTarget } from '../components/AssignmentDialog';
import { adminName, type Portfolio } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { consultationReason } from '@/lib/consultation-reason';

/**
 * Who manages how many merchants (superadmin; lots P1-P3). One line per administrator - an administrator with an empty portfolio is
 * listed too - and ONE line for the shops nobody manages.
 *
 * What a superadmin does here: look at a portfolio (the merchants list, narrowed to that administrator or to the unassigned shops) and
 * TRANSFER a whole portfolio to another administrator, which is the step that unblocks disabling or removing the one who leaves.
 * Approving or refusing a shop is not here, nor anywhere in this console for a superadmin: the managing administrator decides.
 *
 * Every read of cross-organisation data is audited with a reason: the console's default one, or the one typed in the field.
 */
export function PortfoliosPage(): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [dialog, setDialog] = useState<AssignmentTarget | null>(null);
  const query = usePortfoliosQuery(consultationReason(reason));

  const rows = query.data ?? [];
  const admins = rows.filter((row) => row.adminUserId !== null);
  const unassigned = rows.find((row) => row.adminUserId === null);

  function viewMerchants(scope: string): void {
    void navigate(`/merchants?scope=${encodeURIComponent(scope)}`);
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('admin.portfolios.title')}</h1>
        <p className="text-sm text-inkFaint">{t('admin.portfolios.subtitle')}</p>
      </div>

      <div className="rounded-xl border border-line bg-surface p-4">
        <Field label={t('admin.common.reasonConsultationLabel')}>
          {(bind) => (
            <input
              {...bind}
              type="text"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              maxLength={500}
              className="ecom-input"
              placeholder={t('admin.merchants.reasonPlaceholder')}
            />
          )}
        </Field>
      </div>

      {query.isPending ? (
        <TableSkeleton columns={6} />
      ) : query.isError ? (
        <EmptyState
          title={t('admin.portfolios.loadError')}
          description={t(resolveErrorMessageKey(query.error))}
        />
      ) : rows.length === 0 ? (
        <EmptyState title={t('admin.portfolios.empty')} />
      ) : (
        <Table caption={t('admin.portfolios.title')}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('admin.portfolios.colAdmin')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.portfolios.colTotal')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.portfolios.colPending')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.portfolios.colActive')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.portfolios.colSuspended')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.portfolios.colRefused')}</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unassigned === undefined ? null : (
              <TableRow key="unassigned">
                <TableCell>
                  <span className="text-sm font-medium text-ink">
                    {t('admin.portfolios.unassignedRow')}
                  </span>
                  <p className="text-xs text-inkFaint">{t('admin.portfolios.unassignedRowHint')}</p>
                </TableCell>
                <Counts portfolio={unassigned} />
                <TableCell align="end">
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={unassigned.merchantCount === 0}
                      onClick={() => {
                        viewMerchants('unassigned');
                      }}
                    >
                      {t('admin.portfolios.assignUnassigned')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {admins.map((portfolio) => (
              <TableRow key={portfolio.adminUserId}>
                <TableCell>
                  <span className="text-sm text-ink">{adminName(portfolio)}</span>
                  {portfolio.adminStatus !== 'active' ? (
                    <span className="ms-2">
                      <Badge tone="critical">{t('admin.portfolios.disabledBadge')}</Badge>
                    </span>
                  ) : null}
                  {portfolio.displayName === null ? null : (
                    <p className="text-xs text-inkFaint">{portfolio.email}</p>
                  )}
                </TableCell>
                <Counts portfolio={portfolio} />
                <TableCell align="end">
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={portfolio.merchantCount === 0}
                      onClick={() => {
                        viewMerchants(portfolio.adminUserId ?? '');
                      }}
                    >
                      {t('admin.portfolios.viewMerchants')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={portfolio.merchantCount === 0}
                      onClick={() => {
                        if (portfolio.adminUserId === null) return;
                        setDialog({
                          kind: 'portfolio',
                          fromAdminId: portfolio.adminUserId,
                          fromName: adminName(portfolio),
                          count: portfolio.merchantCount,
                        });
                      }}
                    >
                      {t('admin.portfolios.transfer')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AssignmentDialog
        target={dialog}
        onClose={() => {
          setDialog(null);
        }}
      />
    </div>
  );
}

function Counts({ portfolio }: { portfolio: Portfolio }): ReactElement {
  return (
    <>
      <TableCell>
        <span className="text-sm font-medium text-ink">{portfolio.merchantCount}</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-inkSoft">{portfolio.pendingCount}</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-inkSoft">{portfolio.activeCount}</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-inkSoft">{portfolio.suspendedCount}</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-inkSoft">{portfolio.refusedCount}</span>
      </TableCell>
    </>
  );
}
